import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Clock, Lightbulb, CheckCircle, XCircle, ArrowLeft, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Quiz settings
const QUIZ_TIME_LIMIT = 90; // seconds per question
const QUESTIONS_PER_QUIZ = 20; // questions to complete quiz

export default function QuizMode() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [timer, setTimer] = useState(QUIZ_TIME_LIMIT);
  const [loading, setLoading] = useState(true);
  const [showHint, setShowHint] = useState(false);
  const [hint, setHint] = useState('');
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [totalXpEarned, setTotalXpEarned] = useState(0);

  useEffect(() => {
    startGame();
  }, []);

  useEffect(() => {
    if (timer > 0 && !showResult && question && !quizCompleted) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0 && !showResult && !quizCompleted) {
      handleTimeout();
    }
  }, [timer, showResult, question, quizCompleted]);

  // Prevent copying
  useEffect(() => {
    const preventCopy = (e) => {
      e.preventDefault();
      return false;
    };
    
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);
    document.addEventListener('contextmenu', preventCopy);
    
    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
      document.removeEventListener('contextmenu', preventCopy);
    };
  }, []);

  const startGame = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/game/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode: 'quiz' })
      });
      const data = await response.json();
      setSessionId(data.session_id);
      await loadQuestion(data.session_id);
    } catch (error) {
      toast.error('Помилка запуску гри');
      setLoading(false);
    }
  };

  const loadQuestion = async (sid) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/game/question/${sid}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setQuestion(data);
      setTimer(QUIZ_TIME_LIMIT);
      setLoading(false);
    } catch (error) {
      toast.error('Помилка завантаження питання');
    }
  };

  const handleTimeout = () => {
    toast.error('Час вийшов!');
    submitAnswer(-1);
  };

  const submitAnswer = async (answerIndex) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/game/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          question_id: question.question_id,
          answer_index: answerIndex
        })
      });
      const data = await response.json();
      setResult(data);
      setShowResult(true);
      setQuestionsAnswered(prev => prev + 1);
      setTotalXpEarned(prev => prev + (data.xp_earned || 0));
      
      if (data.correct) {
        setCorrectCount(prev => prev + 1);
        toast.success(`+${data.xp_earned} XP!`);
      }
      
      // Check if quiz is completed
      if (questionsAnswered + 1 >= QUESTIONS_PER_QUIZ) {
        setQuizCompleted(true);
      }
    } catch (error) {
      toast.error('Помилка відправки відповіді');
    }
  };

  const handleAnswer = (index) => {
    if (showResult || quizCompleted) return;
    setSelectedAnswer(index);
    submitAnswer(index);
  };

  const nextQuestion = () => {
    if (quizCompleted) return;
    setShowResult(false);
    setSelectedAnswer(null);
    setResult(null);
    setShowHint(false);
    setHint('');
    loadQuestion(sessionId);
  };

  const getHint = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/game/hint/${question.question_id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setHint(data.hint);
      setShowHint(true);
    } catch (error) {
      toast.error('Помилка отримання підказки');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Quiz completed screen
  if (quizCompleted) {
    const accuracy = Math.round((correctCount / QUESTIONS_PER_QUIZ) * 100);
    const grade = accuracy >= 90 ? 'Відмінно!' : accuracy >= 70 ? 'Добре!' : accuracy >= 50 ? 'Непогано' : 'Потрібно більше практики';
    
    return (
      <div className="min-h-screen bg-background">
        <div className="grid-background min-h-screen flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg w-full bg-card/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold text-foreground mb-2">Квіз завершено!</h2>
            <p className="text-xl text-primary mb-6">{grade}</p>
            
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-3xl font-bold text-foreground">{correctCount}/{QUESTIONS_PER_QUIZ}</div>
                <div className="text-sm text-muted-foreground">Правильних відповідей</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-3xl font-bold text-accent">{accuracy}%</div>
                <div className="text-sm text-muted-foreground">Точність</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 col-span-2">
                <div className="text-3xl font-bold text-secondary">+{totalXpEarned} XP</div>
                <div className="text-sm text-muted-foreground">Зароблено досвіду</div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => {
                  setQuizCompleted(false);
                  setQuestionsAnswered(0);
                  setCorrectCount(0);
                  setTotalXpEarned(0);
                  startGame();
                }}
                className="flex-1 bg-gradient-to-r from-primary to-secondary"
                data-testid="play-again-button"
              >
                Грати ще раз
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex-1"
                data-testid="back-to-dashboard"
              >
                На головну
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-background select-none"
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <span className={`text-lg font-bold ${timer < 30 ? 'text-destructive animate-pulse' : 'text-foreground'}`}>
                    {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  <span className="text-primary font-bold">{questionsAnswered + 1}</span>/{QUESTIONS_PER_QUIZ} | 
                  <span className="text-accent ml-1">{correctCount}</span> ✓
                </div>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Question Card */}
            <div className="relative">
              {/* Decorative gradient border */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary via-secondary to-accent rounded-2xl opacity-30 blur-sm"></div>
              
              <div className="relative bg-card/80 backdrop-blur-sm border border-white/10 p-6 md:p-8 rounded-2xl">
                {/* Category and difficulty badges */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider bg-primary/10 px-3 py-1 rounded-full">
                    {question?.category}
                  </span>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                    question?.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-400' :
                    question?.difficulty === 'Advanced' ? 'bg-yellow-500/20 text-yellow-400' :
                    question?.difficulty === 'Expert' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {question?.difficulty}
                  </span>
                </div>
                
                {/* Question text with special styling */}
                <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl p-5 mb-4">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
                    {question?.question_text}
                  </h2>
                </div>
                
                {/* Timer progress bar */}
                <div className="relative">
                  <Progress value={(timer / QUIZ_TIME_LIMIT) * 100} className="h-1.5" />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-muted-foreground">Залишилось часу</span>
                    <span className="text-xs text-muted-foreground">{QUESTIONS_PER_QUIZ - questionsAnswered - 1} питань залишилось</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Answer Options */}
            <div className="space-y-3">
              {question?.options.map((option, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: showResult ? 1 : 1.01, x: showResult ? 0 : 4 }}
                  whileTap={{ scale: showResult ? 1 : 0.99 }}
                  onClick={() => handleAnswer(index)}
                  data-testid={`option-${index}`}
                  disabled={showResult}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all select-none ${
                    showResult
                      ? index === result?.correct_answer
                        ? 'border-accent bg-accent/10 shadow-[0_0_20px_rgba(16,185,129,0.2)]'
                        : selectedAnswer === index
                          ? 'border-destructive bg-destructive/10'
                          : 'border-border/30 bg-card/20 opacity-50'
                      : selectedAnswer === index
                        ? 'border-primary bg-primary/10'
                        : 'border-border/50 bg-card/30 hover:border-primary/50 hover:bg-card/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                        showResult
                          ? index === result?.correct_answer
                            ? 'bg-accent text-white'
                            : selectedAnswer === index
                              ? 'bg-destructive text-white'
                              : 'bg-white/10 text-muted-foreground'
                          : 'bg-white/10 text-foreground'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-foreground font-medium text-sm md:text-base">{option}</span>
                    </div>
                    {showResult && (
                      index === result?.correct_answer ? (
                        <CheckCircle className="w-6 h-6 text-accent" />
                      ) : selectedAnswer === index ? (
                        <XCircle className="w-6 h-6 text-destructive" />
                      ) : null
                    )}
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Result and explanation */}
            {showResult && result && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-5 rounded-xl border ${
                  result.correct 
                    ? 'border-accent/30 bg-accent/5' 
                    : 'border-destructive/30 bg-destructive/5'
                }`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  {result.correct ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-accent" />
                      <span className="font-bold text-accent">Правильно! +{result.xp_earned} XP</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-destructive" />
                      <span className="font-bold text-destructive">Неправильно</span>
                    </>
                  )}
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">{result.explanation}</p>
              </motion.div>
            )}

            {/* Hint section */}
            {showHint && hint && !showResult && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl border border-secondary/30 bg-secondary/10"
              >
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="w-5 h-5 text-secondary" />
                  <span className="font-bold text-secondary">Підказка</span>
                </div>
                <p className="text-sm text-foreground/80">{hint}</p>
              </motion.div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              {!showResult && !showHint && (
                <Button
                  variant="outline"
                  onClick={getHint}
                  className="sm:flex-1"
                  data-testid="hint-button"
                >
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Підказка
                </Button>
              )}
              {showResult && (
                <Button
                  onClick={nextQuestion}
                  className="flex-1 bg-gradient-to-r from-primary to-secondary"
                  data-testid="next-question-button"
                >
                  {questionsAnswered >= QUESTIONS_PER_QUIZ - 1 ? 'Завершити квіз' : 'Наступне питання'}
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
